import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormGroup, FormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { HubConnection, HubConnectionState } from '@microsoft/signalr';
import { Subscription } from 'rxjs';
import { asArray, ClientStatus, CompanySource, TenantStatus, UserAction } from 'src/app/enum';
import { RouteNavigator } from 'src/app/_helpers';
import { SignalRService } from 'src/app/_services';
import { ClientsService, IClientCard, IClientFormData } from 'src/app/_services/clients.service';
import { ClientState } from 'src/app/modules/clients/state/client.state';

@Component({
  selector: 'client-details-card',
  templateUrl: './client-details.card.html',
  styleUrls: ['./client-details.card.scss']
})
export class ClientDetailsCard implements OnInit {

  public edit = false;
  public saveLoading = false;

  public clientCard: IClientCard;
  public formData: IClientFormData;

  public UserAction = UserAction;

  @Input() public clientId;
  @Input() public expanded = false;

  @Output() public onSave: EventEmitter<string> = new EventEmitter<string>();

  public clientStatusOptions = asArray(ClientStatus);

  private _snapshot;
  
  public form = new FormGroup({
    clientAc: new FormControl(),
    clientRegCode: new FormControl(),
    clientKMKR: new FormControl(),
    clientCountryIso: new FormControl(),
    clientAddress: new FormControl(),
    clientAddressText: new FormControl(),
    clientIsFIEOrForeign: new FormControl(false)
  })

  public get clientName() {
    return this.form.get('clientName').value;
  }

  public isClient = false;

  constructor(
    private _routeNavigator: RouteNavigator,
    private _clientsService: ClientsService,
    private _signalRService: SignalRService,
    private _router: Router,
    private _route: ActivatedRoute,
    private _state: ClientState) {
      this.clientCard = this._route.snapshot.data.initialData?.clientCard;

      if (!this.clientCard) {
        return;
      }

      this.formData = this.clientCard.formData;
      const clientInfo = this.clientCard.clientInfo;

      this.isClient = this.clientCard?.clientInfo?.isOwnClient === true;

      this.form.patchValue({
        clientAc: clientInfo.regNo ? {
          regNo: clientInfo.regNo
        } : null,
        clientRegCode: clientInfo.regNo,
        clientKMKR: clientInfo.kmkrNr,
        clientIsFIEOrForeign: clientInfo.isFIEOrForeign,
        clientAddressText: clientInfo.address,
        clientCountryIso: clientInfo.country?.iso
      });

      this.form.get('clientAc').valueChanges.subscribe(x => {
        this.form.patchValue({
          clientRegCode: x?.regNo,
          clientAddressText: x?.address,
          clientIsFIEOrForeign: x?.isFIEOrForeign,
          clientKMKR: null
        });
      });

      this.form.get('clientAddress').valueChanges.subscribe((value) => {
        if (!value?.address_components) {
          return;
        }

        const filteredArray = value.address_components.filter((address_component) => {
          return address_component.types.includes('country');
        });

        if (!filteredArray.length) {
          return;
        }

        this.form.get('clientCountryIso').patchValue(filteredArray[0].short_name, { emitEvent: false });
      });
    }

  ngOnInit() {
    if (this.clientId) {
      this.saveInitialData();
    }
  }

  public get canEdit() {
    return this._state.isClient 
      && this._state.canEdit 
      && this.clientCard.clientInfo.status === TenantStatus.Rejected;
  }

  public backToList() {
    this._routeNavigator.toPreviousRoute({ fallback: '/clients' });
  }

  public toggleEdit() {
    if (this.edit) {
      this.revertToInitialData();
    }

    this.edit = !this.edit;
  }

  public saveInitialData() {
    this._snapshot = this.form.value;
  }

  public revertToInitialData() {
    this.form.patchValue(this._snapshot);
  }

  public focus() {
    this.edit = true;
    this.expanded = true;
  }

  public update() {
    this.clientCard = this._state.clientCard;
  }

  public save() {
    this.saveLoading = true;
    let req = this._createSaveRequest();
    
    this._clientsService
      .saveClientDetails(this.clientId, req)
      .result(this.form, id => {
        if (this.clientId !== id) {
          this._router.navigateByUrl('/clients/empty', { skipLocationChange: true })
            .then(() => this._router.navigate(['/clients', id], { replaceUrl: true }));
          return;
        }

        this.saveLoading = false;
        this.saveInitialData();
        this.edit = false;

        this.onSave.emit(id);
      }, error => { this.saveLoading = false; });
  }

  public getLabel(item: any, isLabel: boolean) {
    return isLabel ? item.regNo : item.name;
  }

  public addNewCompany = (regNo) => {
    return new Promise((resolve) => {
      const newCompany = {
        id: null,
        name: null,
        address: null,
        regNo: regNo
      };
      resolve(newCompany);
    });
  };

  public onAcClear(result: boolean) {
    this.form.reset({
      
    }, {
      emitEvent: false
    });
  }

  private _createSaveRequest() {
    const form = this.form.value;
    const req = {
      regNo: form.clientRegCode,
      kmkrNr: form.clientKMKR,
      isFieOrForeign: form.clientIsFIEOrForeign,
      address: form.clientAddressText,
      countryIso: form.clientCountryIso
    };

    return req;
  }
}